import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  SiteService,
  AxiosErrorResponse,
  PaginationParams,
  PaginationMeta,
} from "@/lib/api/services/siteService";

export interface SiteMember {
  id: number;
  email: string;
  name: string;
  status: string;
  role: string;
  dateJoined: string | null;
  createdAt: string;
  profilePicture: string | null;
  userId: number | null;
}

export interface SiteMemberState {
  members: SiteMember[];
  pagination: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedSiteId: number | null;
}

const defaultInitialState: SiteMemberState = {
  members: [],
  pagination: null,
  isLoading: false,
  error: null,
  successMessage: null,
  selectedSiteId: null,
};

const initialState: SiteMemberState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export const getSiteMembers = createAsyncThunk(
  "siteMember/getMembers",
  async (
    {
      organizationId,
      siteId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      params?: PaginationParams;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.getSiteMembers(organizationId, siteId, params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const inviteSiteMembers = createAsyncThunk(
  "siteMember/inviteMembers",
  async (
    {
      organizationId,
      siteId,
      emails,
      role,
    }: {
      organizationId: number;
      siteId: number;
      emails: string[];
      role: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.inviteSiteMembers(
        organizationId,
        siteId,
        emails,
        role
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const resendSiteMemberInvitation = createAsyncThunk(
  "siteMember/resendInvitation",
  async (
    {
      organizationId,
      siteId,
      memberId,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.resendMemberInvitation(
        organizationId,
        siteId,
        memberId
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const removeSiteMember = createAsyncThunk(
  "siteMember/removeMember",
  async (
    {
      organizationId,
      siteId,
      memberId,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.removeSiteMember(
        organizationId,
        siteId,
        memberId
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const updateSiteMemberRole = createAsyncThunk(
  "siteMember/updateRole",
  async (
    {
      organizationId,
      siteId,
      memberId,
      role,
    }: {
      organizationId: number;
      siteId: number;
      memberId: number;
      role: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.updateMemberRole(
        organizationId,
        siteId,
        memberId,
        role
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const siteMemberSlice = createSlice({
  name: "siteMember",
  initialState,
  reducers: {
    setSelectedSite: (state, action: PayloadAction<number>) => {
      state.selectedSiteId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetSiteMembers: (state) => {
      state.members = [];
      state.pagination = null;
      state.isLoading = false;
      state.error = null;
      state.successMessage = null;
      state.selectedSiteId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSiteMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload.members;
        state.pagination = action.payload.pagination;
      })
      .addCase(getSiteMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(inviteSiteMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(inviteSiteMembers.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = "Invitations sent successfully";
      })
      .addCase(inviteSiteMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resendSiteMemberInvitation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendSiteMemberInvitation.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = "Invitation resent successfully";
      })
      .addCase(resendSiteMemberInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(removeSiteMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeSiteMember.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = "Member removed successfully";
      })
      .addCase(removeSiteMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSiteMemberRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSiteMemberRole.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = "Member role updated successfully";
      })
      .addCase(updateSiteMemberRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedSite,
  clearError,
  clearSuccessMessage,
  resetSiteMembers,
} = siteMemberSlice.actions;
export default siteMemberSlice.reducer;
